import { ErrorBoundary } from '@mentimeter/errors/ErrorBoundary';
import { HotkeyProvider } from '@mentimeter/hotkeys';
import type { ModuleId } from '@mentimeter/http-clients';
import type { SlideState } from '@mentimeter/presentation-state';
import type {
  PresentationSDKT,
  RegisteredHotkeysT,
  SetSlideState,
} from '@mentimeter/question-modules-types';
import { ScreenshotTrigger } from '@mentimeter/screenshots/ScreenshotTrigger';
import { PresentationContext } from '@mentimeter/slides-presentation/contexts/presentation-context';
import { SlideImageLayout } from '@mentimeter/slides-presentation/layout';
import {
  getLayoutSizeOverride,
  getOverrideToFullBackgroundImageWithSize,
} from '@mentimeter/slides-presentation/layout/utils';
import { getPresentationModuleById } from '@mentimeter/slides-presentation/presentation';
import React, { useMemo, type ComponentType } from 'react';
import type { Slide } from '@core-api/presentation/types/response';
import {
  useOptionalPace,
  usePresentation,
  useQfa as useQfaSfinx,
  useQuestion,
  useSessions,
  useConfig as useSfinxConfig,
  useTrendsResults,
  useVoteCode,
} from '../data-mediation';
import { useQuestionModuleResults } from '../data-mediation/results';
import { usePresentationSettings } from '../data-mediation/series/usePresentationSettings';
import fontOffset from '../slide-layout/fontOffset';
import { CURRENT_SESSION_INDEX } from '../utils/constants';
import { useInteractiveCanvas } from './interactive-canvas';
import { useModeration as useSfinxModeration } from './moderation';
import { useTestVotesState } from './presentation-actions/testVotes';
import { useSegmentation as useSfinxSegmentation } from './segmentation';
import { SlideError } from './SlideError';

const KEYSTROKES: Array<RegisteredHotkeysT> = [
  'arrowleft',
  'arrowup',
  'arrowdown',
  'arrowright',
  'enter',
  'escape',
  'space',
  's',
];

const useConfig = (readonly: boolean) => {
  const { mode, enableAnimations, enableHotkeys, enableEmbedSlides } =
    useSfinxConfig();
  return {
    mode,
    enableAnimations,
    enableHotkeys,
    enableEmbedSlides,
    readonly,
  };
};

const useVotingInstructions = () => {
  const { baseUrl, votingUrl } = useSfinxConfig();
  const { participationKey } = usePresentationSettings();
  const voteCode = useVoteCode();
  return {
    baseUrl,
    votingUrl,
    voteKey: participationKey,
    voteCode,
  };
};

const useTrends = () => {
  const { showTrends } = usePresentation();
  const results = useTrendsResults();

  return {
    results,
    showTrends,
  };
};

export const useModeration: PresentationSDKT['useModeration'] = () => {
  const { toggleWord, moderationEnabled } = useSfinxModeration();
  const question = useQuestion();
  if (question.type !== 'open' && question.type !== 'wordcloud') {
    throw new Error(`Cant use moderation in question of type ${question.type}`);
  }
  return {
    toggleWord,
    showUi: moderationEnabled,
  };
};

const useSegmentation = () => {
  const { segments, segmentedBy } = useSfinxSegmentation();
  const question = useQuestion();
  if (question.type !== 'choices') {
    throw new Error(
      `Cant use segmentation in question of type ${question.type}`,
    );
  }

  return {
    segments,
    isSegmented: segmentedBy !== undefined,
    segmentedQuestionTitle: segmentedBy?.question,
  };
};

const usePace: PresentationSDKT['usePace'] = () => {
  const {
    state: { step, currentStep, hasNextSlide } = {
      step: 0,
      currentStep: undefined,
      hasNextSlide: false,
    },
    actions: { next, nextStep, nextSlide } = {
      next: () => {},
      nextStep: () => {},
      nextSlide: () => {},
    },
  } = useOptionalPace() ?? {};
  return {
    next: React.useCallback(() => next('SDK'), [next]),
    nextStep: React.useCallback(() => nextStep('SDK'), [nextStep]),
    nextSlide: React.useCallback(() => nextSlide('SDK'), [nextSlide]),
    hasNextSlide,
    step,
    currentStep,
  };
};

const useSlideState: PresentationSDKT['useSlideState'] = <
  T extends SlideState,
>() => {
  const question = useQuestion();
  const { state, actions } = useOptionalPace() ?? {};
  const setSlideStateAction = actions?.setSlideState;

  const slideState = state?.slideStates?.[question.slidePublicKey] ?? {};

  const setSlideState = React.useCallback<SetSlideState<T>>(
    (newSlideState) =>
      setSlideStateAction?.(question.slidePublicKey, newSlideState),
    [setSlideStateAction, question.slidePublicKey],
  );

  return [slideState as T, setSlideState];
};

// We partly pass useSeries to the SDK, because we need some qfa data from series.
// And the idea is to move this data to the QFA endpoints in the future.
// But we don't want to pass more series data to SDK, so please if you
// are thinking to do it, change your mind or share your reasons with PX team.
const useSeries = () => {
  const {
    qaQuestionVisibility,
    qaModerationPolicy,
    participationIdentityMode,
    participationIdentityVisibility,
  } = usePresentationSettings();
  const qfa_intercom_enabled = qaQuestionVisibility === 'public';
  const qfa_moderation_enabled = qaModerationPolicy === 'moderated';
  const participation_identity_mode = participationIdentityMode;
  const participation_identity_visibility = participationIdentityVisibility;

  return {
    qfa_intercom_enabled,
    qfa_moderation_enabled,
    participation_identity_mode,
    participation_identity_visibility,
  };
};

// Current sessions is the latest session
// Active session is the session that is being viewed
const useCurrentSession = () => {
  const { presentationSessions, activeSession } = useSessions();
  const currentSession = presentationSessions.find(
    (session) => session.current,
  );

  return {
    isCurrentSessionActive: activeSession === CURRENT_SESSION_INDEX,
    currentSessionId: currentSession?.id ?? 0,
    activeSessionId: activeSession,
  };
};

interface PropsT {
  readonly?: boolean;
  getPresentationModule?: (moduleId: ModuleId) => ComponentType;
}

const Module = React.memo<PropsT>(
  ({
    readonly = false,
    getPresentationModule = getPresentationModuleById,
  }: PropsT) => {
    const question = useQuestion();
    const slide = question as Slide;
    const { testVotesShowing } = useTestVotesState();

    const Presentation = getPresentationModule(question.module_id);

    const InteractiveCanvasComponents = useInteractiveCanvas();

    const SDK = React.useMemo<PresentationSDKT>(
      () => ({
        useQfa: useQfaSfinx,
        useSeries,
        useConfig: () => useConfig(readonly),
        useTrends,
        useResults: useQuestionModuleResults,
        Utils: { fontOffset },
        useModeration,
        usePace,
        useVotingInstructions,
        useSegmentation,
        useCurrentSession,
        useSlideState,
        InteractiveCanvasComponents,
        isPreviewResultsEnabled: testVotesShowing,
      }),
      [InteractiveCanvasComponents, readonly, testVotesShowing],
    );

    /**
     * We're trying to move away from the SDK and the design of passing in hooks through the context.
     * We don't know exactly how the new design will look like, but as a first step we're passing in
     * only the data **needed** for rendering the slide directly through the context.
     */
    const presentationSlide = useMemo(
      () => ({
        slideAdminKey: slide.slideAdminKey,
        slidePublicKey: slide.slidePublicKey,
        title: slide.title,
        staticContent: slide.staticContent,
        interactiveContents: slide.interactiveContents,
        responseVisibility: slide.responseVisibility,
        images: slide.images,
        design: slide.design,
      }),
      [slide],
    );

    const presentationContextValue = useMemo(
      () => ({ sdk: SDK, presentationSlide }),
      [SDK, presentationSlide],
    );

    return (
      <ErrorBoundary feature="slides" fallback={<SlideError />}>
        <HotkeyProvider keyStrokes={KEYSTROKES}>
          <PresentationContext.Provider value={presentationContextValue}>
            <SlideImageLayout
              image={{
                url: question.images?.[0]?.presets.original.url,
                altText: question.image_alt_description,
              }}
              layoutType={question.layout?.type}
              layoutSize={getLayoutSizeOverride(question.layout, question.type)}
              overrideToFullBackgroundImageWithSize={getOverrideToFullBackgroundImageWithSize(
                question.type,
                question.slide_type,
                question.image_as_background,
                question.slide_image_type,
              )}
            >
              <Presentation key={question.id} />
            </SlideImageLayout>
            <ScreenshotTrigger />
          </PresentationContext.Provider>
        </HotkeyProvider>
      </ErrorBoundary>
    );
  },
);

export default Module;
